import { CopyOutlined, DownloadOutlined, ReadOutlined, SoundOutlined, StopOutlined } from '@ant-design/icons';
import { Button as AntButton, message } from 'antd';
import Button from 'antd/lib/button';
import axios from 'axios';
import { format } from 'date-fns';
import { enUS, nb } from 'date-fns/locale';
import parse from 'html-react-parser';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageModal } from '../components/MyImage/ImageModal';
import type { ClickedImageType } from '../components/MyImage/MyImage';
import { envApiUrl } from '../fetchHooks/envApiUrl';

export type CopyToClipboardButtonProps = {
  text: string;
};

export const CopyToClipboardButton: React.FC<CopyToClipboardButtonProps> = ({ text }) => (
  <Button
    icon={<CopyOutlined />}
    shape="round"
    size="small"
    style={{ border: 0, right: 8, position: 'absolute' }}
    onClick={async () => {
      await navigator.clipboard.writeText(text);
      message.success('Copied to clipboard');
    }}
  />
);

export type WaitForNSecondsFunc = () => void;

export function waitForNSeconds(n: number, func?: WaitForNSecondsFunc): void {
  const start = performance.now();

  function wait(timestamp: number): void {
    if (timestamp - start < n * 1000) {
      requestAnimationFrame(wait);
    } else {
      func?.();
    }
  }

  requestAnimationFrame(wait);
}

export function addHttps(url: string): string {
  const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/;

  if (urlRegex.test(url)) {
    if (url.startsWith('http://')) {
      return url.replace('http://', 'https://');
    }
    return 'https://' + url;
  }
  return url;
}

export const formatDateFull = (date: Date, locale = 'nb'): string => {
  const dateLocale = locale === 'en' ? enUS : nb;
  return format(date, "eee d. MMM yyyy 'kl.' HH:mm", { locale: dateLocale });
};

export const formatTimeOnly = (date: Date, locale = 'nb'): string => {
  const dateLocale = locale === 'en' ? enUS : nb;
  return format(date, "'kl.' HH:mm", { locale: dateLocale });
};

export const formatDateOnly = (date: Date, locale = 'nb'): string => {
  const dateLocale = locale === 'en' ? enUS : nb;
  return format(date, "eee d. MMM ''yy", { locale: dateLocale });
};

export const formatWeekdayOnly = (date: Date, locale = 'nb'): string => {
  const dateLocale = locale === 'en' ? enUS : nb;
  return format(date, 'eee', { locale: dateLocale });
};

/**
 * Get the localized description for a health metric type
 * Returns description_en if language is English and description_en exists, otherwise returns description
 */
export function getHealthMetricDescription(
  healthMetric: { description?: string; description_en?: string } | undefined,
  language = 'nb',
): string {
  if (!healthMetric) return '';
  if (language === 'en' && healthMetric.description_en) {
    return healthMetric.description_en;
  }
  return healthMetric.description || '';
}

export type SensitiveTextFieldProps = {
  text?: string | null;
  show: boolean;
  onClick?: () => void;
};

export const SensitiveTextField: React.FC<SensitiveTextFieldProps> = ({ text, show, onClick }) => {
  if (!text) return null;
  return (
    <span
      style={{ cursor: 'pointer' }}
      onClick={() => onClick?.()}
      onKeyUp={(e: React.KeyboardEvent<HTMLSpanElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick?.();
        }
      }}
    >
      {show ? text : text.replace(/./g, '*')}
    </span>
  );
};

export type SensitiveRichTextFieldProps = {
  text?: string | null;
  show: boolean;
  onClick?: () => void;
  className?: string;
};

export const SensitiveRichTextField: React.FC<SensitiveRichTextFieldProps> = ({ text, show, onClick, className }) => {
  if (!text) return null;

  return (
    <span
      style={{ cursor: 'pointer' }}
      onClick={() => onClick?.()}
      onKeyUp={(e: React.KeyboardEvent<HTMLSpanElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick?.();
        }
      }}
    >
      {show ? (
        <div>{parse(text)}</div> // Safely parse and render HTML
      ) : (
        <div className={className} style={{ overflowWrap: 'break-word' }}>
          {text.replace(/./g, '*')}
        </div>
      )}
    </span>
  );
};

export type RichTextFieldProps = {
  text?: string | null;
  onClick?: () => void;
  className?: string;
};

export const RichTextField: React.FC<RichTextFieldProps> = ({ text, onClick, className }) => {
  const { t, i18n } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [clickedImage, setClickedImage] = useState<ClickedImageType | undefined>();
  const allImagesRef = useRef<HTMLImageElement[]>([]);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [speakingText, setSpeakingText] = useState<string>('');
  const [isGeneratingAudio, setIsGeneratingAudio] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Cleanup: stop audio when component unmounts
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Convert HTML to plain text (remove HTML tags and markdown syntax)
  const htmlToPlainText = (html: string): string => {
    // Create a temporary div to extract text from HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    let text = tempDiv.textContent || tempDiv.innerText || '';

    // Remove extra whitespace
    text = text.replace(/\n{3,}/g, '\n\n');
    text = text.trim();
    return text;
  };

  const handleSpeak = async (textToSpeak: string) => {
    // Stop any ongoing speech
    if (isSpeaking && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsSpeaking(false);
      setSpeakingText('');
      return;
    }

    // Convert HTML to plain text
    const plainText = htmlToPlainText(textToSpeak);

    if (!plainText.trim()) {
      return;
    }

    setIsGeneratingAudio(true);

    try {
      // Get CSRF token
      const csrfResponse = await axios.get(envApiUrl + '/auth/csrf-token', {
        withCredentials: true,
      });
      const csrfToken = csrfResponse.data.csrfToken;

      // Call backend TTS endpoint
      const response = await axios.post(
        envApiUrl + '/mydays/text-to-speech',
        {
          text: plainText,
          language: i18n.language === 'nb' ? 'no' : 'en',
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': csrfToken,
          },
          withCredentials: true,
          responseType: 'blob',
        },
      );

      // Create audio element and play
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onplay = () => {
        setIsSpeaking(true);
        setSpeakingText(textToSpeak);
        setIsGeneratingAudio(false);
      };

      audio.onended = () => {
        setIsSpeaking(false);
        setSpeakingText('');
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };

      audio.onerror = () => {
        setIsSpeaking(false);
        setSpeakingText('');
        setIsGeneratingAudio(false);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };

      await audio.play();
    } catch (err: any) {
      console.error('Error generating or playing speech:', err);
      setIsGeneratingAudio(false);

      // If OpenAI TTS is not available, fall back to Web Speech API
      if (err.response?.status === 503 && err.response?.data?.code === 'TTS_NOT_AVAILABLE') {
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
          const plainText = htmlToPlainText(textToSpeak);
          const utterance = new SpeechSynthesisUtterance(plainText);
          utterance.lang = i18n.language === 'nb' ? 'nb-NO' : 'en-US';

          utterance.onstart = () => {
            setIsSpeaking(true);
            setSpeakingText(textToSpeak);
          };

          utterance.onend = () => {
            setIsSpeaking(false);
            setSpeakingText('');
          };

          utterance.onerror = () => {
            setIsSpeaking(false);
            setSpeakingText('');
          };

          window.speechSynthesis.speak(utterance);
        }
      }
    }
  };

  const handleStopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsSpeaking(false);
      setSpeakingText('');
    }
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  };

  // Keyboard navigation functions
  const goToNextImage = useCallback(() => {
    if (!clickedImage || clickedImage.index === undefined || allImagesRef.current.length === 0) return;
    const newIndex = (clickedImage.index + 1) % allImagesRef.current.length;
    const img = allImagesRef.current[newIndex];
    setClickedImage({
      src: img.src,
      name: img.alt || `Image ${newIndex + 1}`,
      description: img.alt || '',
      index: newIndex,
      uncompressedImageSrc: img.src,
    });
  }, [clickedImage]);

  const goToPrevImage = useCallback(() => {
    if (!clickedImage || clickedImage.index === undefined || allImagesRef.current.length === 0) return;
    const newIndex = clickedImage.index === 0 ? allImagesRef.current.length - 1 : clickedImage.index - 1;
    const img = allImagesRef.current[newIndex];
    setClickedImage({
      src: img.src,
      name: img.alt || `Image ${newIndex + 1}`,
      description: img.alt || '',
      index: newIndex,
      uncompressedImageSrc: img.src,
    });
  }, [clickedImage]);

  // Add keyboard event listeners
  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        goToNextImage();
      } else if (event.key === 'ArrowLeft') {
        goToPrevImage();
      } else if (event.key === 'Escape') {
        setShowModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal, goToNextImage, goToPrevImage]);

  // Early return AFTER all hooks have been called
  if (!text) return null;

  // Convert width attributes to style attributes before parsing
  const processedText = text.replace(/<img([^>]*)\s+width="([^"]*)"([^>]*)>/g, (_, before, width, after) => {
    return `<img${before} style="width: ${width};"${after}>`;
  });

  // Use callback ref to manipulate DOM immediately
  const handleRef = (node: HTMLDivElement | null) => {
    if (node) {
      const imagesWithWidth = node.querySelectorAll('img[width]');
      const allImages = node.querySelectorAll('img');

      // Process images with width attributes
      imagesWithWidth.forEach((img) => {
        const width = img.getAttribute('width');
        if (width && img instanceof HTMLImageElement) {
          img.style.width = width;
          img.style.maxWidth = '100%';
        }
      });

      // Store images array for navigation
      allImagesRef.current = Array.from(allImages);

      // Make images clickable
      allImages.forEach((img, index) => {
        img.style.cursor = 'pointer';
        img.addEventListener('click', (e) => {
          e.stopPropagation();
          const src = img.src;
          const alt = img.alt || '';

          // Extract image ID from URL if it's a local image
          const imageIdMatch = src.match(/\/files\/image\/(\d+)/);
          const imageId = imageIdMatch ? parseInt(imageIdMatch[1]) : null;

          const uncompressedImageSrc = imageId ? `${envApiUrl}/files/image/${imageId}` : src;

          setClickedImage({
            src: uncompressedImageSrc,
            name: alt || `Image ${index + 1}`,
            description: alt || '',
            index: index,
            uncompressedImageSrc: uncompressedImageSrc,
          });
          setShowModal(true);
        });
      });
    }
  };

  return (
    <>
      <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
        {text?.trim() && (
          <AntButton
            size="small"
            icon={isGeneratingAudio || (isSpeaking && speakingText === text) ? <StopOutlined /> : <SoundOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              if (isGeneratingAudio || (isSpeaking && speakingText === text)) {
                handleStopSpeaking();
              } else {
                void handleSpeak(text);
              }
            }}
            loading={isGeneratingAudio && speakingText === text}
            type={isGeneratingAudio || (isSpeaking && speakingText === text) ? 'default' : 'text'}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 10,
            }}
            title={
              isGeneratingAudio && speakingText === text
                ? t('myday.generatingAudio') || 'Generating...'
                : isSpeaking && speakingText === text
                  ? t('myday.stopReading') || 'Stop Reading'
                  : t('myday.readAloud') || 'Read Aloud'
            }
          />
        )}
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => onClick?.()}
          onKeyUp={(e: React.KeyboardEvent<HTMLSpanElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onClick?.();
            }
          }}
        >
          <div ref={handleRef} className={className || ''}>
            {parse(processedText)}
          </div>{' '}
          {/* Safely parse and render HTML */}
        </span>
      </div>
      {showModal && clickedImage && (
        <ImageModal
          clickedImage={clickedImage}
          onClose={() => setShowModal(false)}
          onClick={goToNextImage}
          fullImageSrc={clickedImage.src}
        />
      )}
    </>
  );
};

export type FileDownloadLinkProps = {
  fileId: number;
  fileName: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  isPdf?: boolean;
};

export function FileDownloadLink({ fileId, fileName, onClick, isPdf = false }: FileDownloadLinkProps) {
  const downloadUrl = `${envApiUrl}/files/download/${fileId}`;

  if (isPdf) {
    return <Button onClick={onClick} icon={<ReadOutlined />} />;
  }
  return (
    <a href={downloadUrl} download={fileName} onClick={onClick}>
      <DownloadOutlined />
    </a>
  );
}
